import React from "react";
import { registerLogout } from "../../../api/logoutHelper";
import { NavModule } from "../../../config/navigationConfig";
import { IAuth, ILogin, IUser, IUsuarioRolContexto} from "../model/modelAuth";
import useSession from "./useSession";
import { WSLogin, WSContextSelection } from "../api/authApi";

/**
 * Administra el estado y las operaciones principales
 * relacionadas con la autenticación del usuario.
 *
 * Responsabilidades:
 * - Iniciar sesión y obtener los contextos disponibles.
 * - Seleccionar un contexto de trabajo.
 * - Restaurar una sesión almacenada.
 * - Cerrar y limpiar la sesión.
 */
const useAuth = (): IAuth => {

  const [user, setUser] = React.useState<IUser | undefined>(undefined);
  const [navigation, setNavigation] = React.useState<NavModule[]>([]);
  const [selectedNav, setSelectedNav] = React.useState<NavModule | undefined>(undefined);

  /**
   * Indica si la aplicación todavía está intentando
   * restaurar una sesión previamente almacenada.
   */
  const [searchingUser, setSearchingUser] = React.useState(true);

  /**
   * Indica si se está ejecutando una operación de
   * autenticación, como login o selección de contexto.
   */
  const [loaderlogin, setLoaderLogin] = React.useState(false);

  const { establishSession, restoreSession, clearSession} = useSession({setUser, setNavigation, setSelectedNav});

  /**
   * Valida las credenciales del usuario y devuelve
   * los contextos a los que tiene acceso.
   */
  const login = React.useCallback( async (email: string, password: string): Promise<IUsuarioRolContexto[]> => {
      const loginRequest: ILogin = {
        email,
        password,
      };
      try {
        setLoaderLogin(true);
        const contextos = await WSLogin(loginRequest);
        if (contextos.length === 0) {
          throw new Error(
            "No se encontraron contextos asociados a su usuario."
          );
        }
        return contextos;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message: "Error al iniciar sesión.";
        throw new Error(message, {
          cause: error,
        });
      } finally {
        setLoaderLogin(false);
      }
    },
    []
  );

  /**
   * Selecciona el contexto de trabajo del usuario
   * y establece la sesión autenticada.
   */
  const selectContext = React.useCallback(async (usuarioRolContextoId: number): Promise<void> => {
      try {
        setLoaderLogin(true);
        const session = await WSContextSelection(usuarioRolContextoId);
        establishSession(session);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Error al seleccionar el contexto.";
        throw new Error(message, {
          cause: error,
        });
      } finally {
        setLoaderLogin(false);
      }
    },
    [establishSession]
  );

  /**
   * Elimina la sesión local y reinicia el estado
   * relacionado con la autenticación.
   */
  const logout = React.useCallback(() => {
    clearSession();
  }, [clearSession]);

  /**
   * Registra la función de cierre de sesión para que
   * pueda ser utilizada por los interceptores HTTP.
   */
  React.useEffect(() => {
    registerLogout(logout);
  }, [logout]);

  /**
   * Intenta restaurar la sesión almacenada al iniciar
   * la aplicación.
   */
  React.useEffect(() => {
    try {
      restoreSession();
    } catch (error: unknown) {
      console.error("Error al reconstruir la sesión:", error);
      clearSession();
    } finally {
      setSearchingUser(false);
    }
  }, [clearSession, restoreSession]);

  return {
    user,
    navigation,
    selectedNav,
    setSelectedNav,
    searchingUser,
    loaderlogin,
    login,
    selectContext,
    logout,
  };
};

export default useAuth;